import type { Content, Editor } from '@tiptap/core'
import { useEditorState } from '@tiptap/react'
import {
  watchPreviewContent,
  type SnapshotVersion,
} from '@tiptap-pro/extension-snapshot'
import { TiptapCollabProvider } from '@tiptap-pro/provider'
import { intlFormat } from 'date-fns'
import {
  Fragment,
  VFC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Button,
  Checkbox,
  Drawer,
  DrawerOrigin,
  DrawerSize,
  History,
  Pill,
  Toast,
  toast,
  Tooltip,
  type ToastComponent,
} from '@opallabs/lapidary'
import { TextEditor, useTextEditor } from '../../../TextEditor'
import { TextEditorContent } from '../../../TextEditor/content'
import { mergeExtensions } from '../../../TextEditor/hooks'
import * as styles from './styles'

type VersioningModalProps = {
  isOpen: boolean
  hasDraft?: boolean
  onClose: () => void
  provider: TiptapCollabProvider
  versions: SnapshotVersion[]
}

/** Returns the formatted date for a version or draft */
const getFormattedVersionDate = (version: SnapshotVersion | null): string => {
  if (!version) return 'Now'

  return intlFormat(new Date(version.date), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Returns the name of the version(s). */
const getVersionName = (version: SnapshotVersion | null): string => {
  /** Version is a draft */
  if (version === null) {
    return 'Unversioned draft'
  }

  return (
    version.name ||
    (version.version === 0 ? 'Initial version' : `Version ${version.version}`)
  )
}

/** Formats the display of the selected versions in the preview header
 * for both the title heading and date subtitle. */
const getComparisonDisplay = (
  comparisonVersions: SnapshotVersion[],
  selectedVersionData: SnapshotVersion | null,
  isDisplayingComparison: boolean,
  formatter: (version: SnapshotVersion | null) => string
): string => {
  if (comparisonVersions.length === 2 && isDisplayingComparison) {
    /** Sort versions from first to last when two selected and comparison is active */
    const sortedVersions = [...comparisonVersions].sort(
      (a, b) => a.version - b.version
    )
    return `${formatter(sortedVersions[0])} — ${formatter(sortedVersions[1])}`
  }
  /**  Return for one selected version */
  return formatter(selectedVersionData)
}

/** Determines if a version should be disabled based on comparison selection */
const isVersionDisabled = (
  comparisonVersions: SnapshotVersion[],
  targetVersion: number
): boolean => {
  return (
    comparisonVersions.length === 2 &&
    !comparisonVersions.some((c) => c.version === targetVersion)
  )
}

const RevertSuccessToast: ToastComponent<{ revertName: string }> = ({
  revertName,
  ...toastProps
}) => (
  <Toast
    {...toastProps}
    title="Revert complete"
    message={`The document was reverted to ${revertName}.`}
  />
)

const RevertFailedToast: ToastComponent = (toastProps) => (
  <Toast
    {...toastProps}
    title="Revert failed"
    message={`The document could not be reverted due to an error. Please try again.`}
  />
)

/** Draft item component for the version history */
const DraftHistoryItem: VFC<{
  comparisonVersions: SnapshotVersion[]
  selectedVersionId: number | null | undefined
  onSelect: () => void
}> = ({ comparisonVersions, selectedVersionId, onSelect }) => {
  const isDisabled = comparisonVersions.length === 2

  return (
    <History.Item
      key="draft"
      selected={selectedVersionId === null}
      disabled={isDisabled}
      onSelect={onSelect}
      className="texteditor-versioning-draft"
      css={styles.historyItem(isDisabled)}
    >
      <History.Details>
        <History.Title>Unversioned draft</History.Title>
      </History.Details>
      <History.RestorationAction current canRestore={false} />
    </History.Item>
  )
}

export const VersioningModal: VFC<VersioningModalProps> = ({
  isOpen,
  hasDraft = false,
  provider,
  onClose,
  versions,
}) => {
  const previewPaneRef = useRef<HTMLElement>(null)
  /** `parentEditor` is the primary editor */
  const { editor: parentEditor } = useTextEditor()
  /** `previewEditor` is the read-only preview used within this modal */
  const [previewEditor, setPreviewEditor] = useState<Editor | null>(null)
  /**
   * Corresponds to the `version` property of the selected version in `versions`.
   * `null` indicates the selected version is the draft state (not in `versions`.)
   * `undefined` indicates the component state hasn't been initialized yet.
   *
   * The version ID is zero-indexed, so prefer `!= null` checks over truthy.
   */
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>()
  /** `versions` should be displayed in descending order. Use this list for display. */
  const reversedVersions = useMemo(() => versions.slice().reverse(), [versions])
  /** The first two versions in `reversedVersions` used to preselect them when the modal first opens. */
  const defaultComparisonVersions = useMemo(() => {
    return reversedVersions.length >= 2 ? reversedVersions.slice(0, 2) : []
  }, [reversedVersions])
  /** The selected versions used for the diff comparison. */
  const [comparisonVersions, setComparisonVersions] = useState<
    SnapshotVersion[]
  >([])
  const previewState = useEditorState({
    editor: previewEditor,
    selector: (snapshot) => ({
      isDisplayingComparison:
        snapshot.editor?.storage.snapshotCompare?.isPreviewing,
    }),
  })
  const parentState = useEditorState({
    editor: parentEditor,
    selector: (snapshot) => ({ isEditable: snapshot.editor?.isEditable }),
  })

  const compareVersions = () => {
    previewEditor
      ?.chain()
      .compareVersions({
        fromVersion: comparisonVersions[0].version,
        toVersion: comparisonVersions[1].version,
      })
      .run()
  }

  const unselectAllVersions = () => {
    setComparisonVersions([])
    setSelectedVersionId(undefined)
    previewEditor?.commands.hideDiff()
  }

  /** The data for the currently selected version (i.e. highlighted in the sidebar.) */
  const selectedVersionData = useMemo(() => {
    return (
      (selectedVersionId != null &&
        versions.find((v) => v.version === selectedVersionId)) ||
      null
    )
  }, [selectedVersionId, versions])

  /** True if the selected version is the current version of the parent editor. */
  const selectedVersionIsCurrent = useMemo(
    () =>
      hasDraft
        ? selectedVersionId === null
        : reversedVersions.length > 0 &&
          selectedVersionId === reversedVersions[0].version,
    [hasDraft, reversedVersions, selectedVersionId]
  )

  /**
   * Resets the modal on close. No effect when the modal is opened.
   */
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose()
        setComparisonVersions([])
        setSelectedVersionId(undefined)
        previewEditor?.commands.clearContent()
      }
    },
    [onClose, previewEditor]
  )

  /**
   * Handles checkbox change for version comparison selection.
   */
  const onCheckboxChange = useCallback(
    (version: SnapshotVersion) => {
      const selectedIndex = comparisonVersions.findIndex(
        (c) => c.version === version.version
      )
      const newVersions = [...comparisonVersions]
      if (selectedIndex > -1) newVersions.splice(selectedIndex, 1)
      else newVersions.push(version)
      setComparisonVersions(newVersions)
    },
    [comparisonVersions]
  )

  /**
   * Requests the preview content for a version from the collaboration service.
   * The effect below with `watchPreviewContent` watches for the response.
   */
  const requestVersionPreview = useCallback(
    (newVersionId: number) => {
      setSelectedVersionId(newVersionId)
      provider.sendStateless(
        JSON.stringify({
          action: 'version.preview',
          version: newVersionId,
        })
      )

      /** Hides the diff view if it was active. */
      previewEditor?.commands.hideDiff()
    },
    [provider, previewEditor]
  )

  /**
   * Loads the draft state, by setting the preview editor content
   * to the parent editor's content.
   */
  const handleDraftPreview = useCallback(() => {
    if (previewEditor?.commands.setContent(parentEditor.getJSON()) === true) {
      setSelectedVersionId(null)
    }
    previewEditor?.commands.hideDiff()
  }, [previewEditor, parentEditor])

  /**
   * Reverts the content to that of the selected version, and closes the modal.
   * Note that reverting creates a new version on top.
   *
   * If the selected version is the draft, the revert does not occur because
   * the draft is not technically a version and can't be reverted.
   */
  const handleRevert = useCallback(
    (data: SnapshotVersion) => {
      const revertName = data.name
        ? `"${data.name}"`
        : data.version === 0
          ? 'the initial version'
          : `version ${data.version}`

      const isReverted = parentEditor.commands.revertToVersion(
        data.version,
        `Revert to ${revertName}`
      )

      if (isReverted) {
        toast.success(
          (props) => <RevertSuccessToast {...props} revertName={revertName} />,
          {
            toastId: 'texteditor-revert-success',
            closeOnClick: true,
            autoClose: 5000,
          }
        )
      } else {
        toast.error(RevertFailedToast, {
          type: 'error',
          toastId: 'texteditor-revert-failed',
          closeOnClick: true,
          autoClose: 5000,
        })
      }

      handleOpenChange(false)
    },
    [handleOpenChange, parentEditor.commands]
  )

  // When opened, initialize editor with most recent version
  useEffect(() => {
    if (isOpen && previewEditor !== null && selectedVersionId === undefined) {
      if (versions.length && !hasDraft) {
        const initialVersionId = versions.at(-1)?.version
        if (initialVersionId !== undefined) {
          requestVersionPreview(initialVersionId)
        }
      } else {
        handleDraftPreview()
      }
    }
  }, [
    previewEditor,
    selectedVersionId,
    handleDraftPreview,
    requestVersionPreview,
    isOpen,
    hasDraft,
    versions,
  ])

  // When modal opens, preselect the latest two versions
  useEffect(() => {
    if (isOpen && defaultComparisonVersions.length >= 2) {
      setComparisonVersions(defaultComparisonVersions)
    }
  }, [isOpen, defaultComparisonVersions])

  // Updates the preview editor content when the server responds to a
  // 'version.preview' request, invoked by `requestVersionPreview` above
  useEffect(() => {
    let unbindListener: ReturnType<typeof watchPreviewContent> | undefined
    if (isOpen && previewEditor !== null) {
      unbindListener = watchPreviewContent(provider, (content: Content) => {
        previewEditor?.commands.setContent(content)
        previewPaneRef.current?.scrollTo(0, 0)
      })
    }

    return () => {
      unbindListener?.()
    }
  }, [isOpen, provider, previewEditor])

  return (
    <Drawer.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Drawer.Content
        drawerOrigin={DrawerOrigin.Bottom}
        drawerSize={DrawerSize.Full}
        css={styles.modal}
        className="texteditor-versioning-modal"
      >
        <section css={styles.preview} ref={previewPaneRef}>
          <div css={styles.previewHeader}>
            <div>
              <h2 css={styles.previewHeaderTitle}>
                {getComparisonDisplay(
                  comparisonVersions,
                  selectedVersionData,
                  !!previewState?.isDisplayingComparison,
                  getVersionName
                )}
              </h2>
              <span css={styles.previewHeaderDate}>
                {getComparisonDisplay(
                  comparisonVersions,
                  selectedVersionData,
                  !!previewState?.isDisplayingComparison,
                  getFormattedVersionDate
                ) || <Fragment>&nbsp;</Fragment>}
              </span>
            </div>
            {selectedVersionIsCurrent &&
              !!previewState?.isDisplayingComparison && (
                <Pill text="Current" color="green" size="small" solid />
              )}
            {previewState?.isDisplayingComparison && (
              <Pill text="Comparison" color="darkGray" size="small" solid />
            )}
          </div>

          <TextEditor
            readOnly={true}
            onEditorUpdate={setPreviewEditor}
            assetStore={parentEditor.storage.asset.assetStore}
            contentStyle={[styles.content, styles.diffContent]}
            extensions={mergeExtensions({
              centerJustify: { config: true },
              comments: {
                config: {
                  provider,
                  useLegacyWrapping: false,
                  /**
                   * When `deleteUnreferencedThreads` is `true`, thread marks are removed by the editor
                   * which then removes them from the shared provider causing the threads to be
                   * deleted from the original document. This needs to stay `false` for the preview
                   * editor to not delete threads from the original document.
                   */
                  deleteUnreferencedThreads: false,
                },
              },
              mention: { config: true },
              snapshotCompare: {
                config: provider ? { provider } : false,
              },
            })}
          >
            <TextEditorContent />
          </TextEditor>
        </section>
        <section css={styles.history}>
          <Drawer.Header>
            <Drawer.Title>Version History</Drawer.Title>
            <Drawer.Description>
              View, compare, or restore versions of this document.
            </Drawer.Description>
          </Drawer.Header>
          <Drawer.Close />
          <History.Root css={styles.historyList}>
            {hasDraft && (
              <DraftHistoryItem
                comparisonVersions={comparisonVersions}
                selectedVersionId={selectedVersionId}
                onSelect={handleDraftPreview}
              />
            )}
            {reversedVersions.map((v, i) => {
              const isDisabled = isVersionDisabled(
                comparisonVersions,
                v.version
              )
              return (
                <History.Item
                  key={v.version}
                  disabled={isDisabled}
                  selected={selectedVersionId === v.version}
                  onSelect={() => requestVersionPreview(v.version)}
                  className="texteditor-versioning-version"
                  css={styles.historyItem(isDisabled)}
                >
                  <Checkbox
                    checked={
                      comparisonVersions.findIndex(
                        (c) => c.version === v.version
                      ) > -1
                    }
                    disabled={isDisabled}
                    onChange={() => onCheckboxChange(v)}
                  />
                  <History.Details>
                    <History.Title>{getVersionName(v)}</History.Title>
                    <History.SubTitle>
                      {getFormattedVersionDate(v)}
                    </History.SubTitle>
                  </History.Details>
                  <History.RestorationAction
                    current={!hasDraft && i === 0}
                    canRestore={
                      parentState.isEditable &&
                      selectedVersionId === v.version &&
                      versions.length > 1
                    }
                    onRestore={() => parentState.isEditable && handleRevert(v)}
                  />
                </History.Item>
              )
            })}
          </History.Root>
          <div css={styles.footerButtonContainer}>
            <Tooltip
              content="Must select two versions to compare"
              hidden={comparisonVersions.length >= 2}
              sideOffset={4}
              zIndex={9999}
            >
              <Button
                buttonText="Compare Selected"
                disabled={
                  comparisonVersions.length < 2 ||
                  previewState?.isDisplayingComparison
                }
                name="Compare Selected Versions"
                onClick={compareVersions}
              />
            </Tooltip>
            <Button
              buttonText="Unselect All"
              disabled={comparisonVersions.length === 0}
              name="Unselect All"
              onClick={unselectAllVersions}
              variant="tertiary"
            />
          </div>
        </section>
      </Drawer.Content>
    </Drawer.Root>
  )
}
